import { useRef } from 'react'
import textServices from 'services/text-services'
import useMediaQuery from 'hooks/use-media-query'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { CREDENCIALES_INTRO_ID, CREDENCIALES_STORES_ID, credencialesImage } from '../constants'
import { useFloatingDecor } from '../hooks/use-scroll-parallax'

// Decorative art (2x exports, rendered at half size). Positions come from
// the Figma boxes: desktop 5631:1587 (bottle 5631:1592, hop 5631:1625,
// wheat 5631:1606) and mobile 5854:1297 (5867:4186/4188/4190/4191/4192).
// Hops/wheat float (scroll parallax + idle sway); the bottle stays fixed.
const DESKTOP_DECOR = [
  { key: 'hop', kind: 'hop', name: 'flotante_lupulo_desk_1_2x' },
  { key: 'wheat', kind: 'wheat', name: 'flotante_trigo_desk_3_2x' }
]

const MOBILE_DECOR = [
  { key: 'wheat-top', kind: 'wheat', name: 'flotante_trigo_mob_1_2x' },
  { key: 'hop', kind: 'hop', name: 'flotante_lupulo_mob_1_2x' },
  { key: 'wheat-left', kind: 'wheat', name: 'flotante_trigo_mob_3_2x' },
  { key: 'wheat-bottom', kind: 'wheat', name: 'flotante_trigo_mob_3_2x' }
]

interface IntroSectionProps {
  onCtaClick?: () => void
}

// "¿Qué es?" introduction (Figma desktop 5631:1587 zone 0-1650 / mobile
// 5854:1297 zone 0-780): centered column over the green gradient.
const IntroSection = ({ onCtaClick }: IntroSectionProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const folder = isDesktop ? 'DESKTOP' : 'MOBILE'
  const decor = isDesktop ? DESKTOP_DECOR : MOBILE_DECOR

  useFloatingDecor(sectionRef, isDesktop)

  const handleCta = () => {
    dlTrackEvent('user_interaction', {
      action: 'click',
      element: 'intro :: como conseguirla',
      section: 'credenciales'
    })
    if (onCtaClick) return onCtaClick()
    document.getElementById(CREDENCIALES_STORES_ID)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section ref={sectionRef} id={CREDENCIALES_INTRO_ID} className="credenciales__intro">
      <img
        className="credenciales__intro-bottle"
        src={credencialesImage(folder, isDesktop ? 'img_botella_desk_1_2x' : 'img_botella_mob_1_2x')}
        alt=""
        aria-hidden="true"
      />
      {decor.map((item) => (
        <span
          key={item.key}
          className={`credenciales__intro-float credenciales__intro-float--${item.key}`}
          data-float={item.kind}
          aria-hidden="true"
        >
          <img src={credencialesImage(folder, item.name)} alt="" loading="lazy" draggable={false} />
        </span>
      ))}

      <div className="credenciales__intro-content">
        <h1 className="credenciales__intro-title">
          {textServices.findByText('credenciales/intro/title', '¿Qué hace que Heineken tenga un sabor de calidad?')}
        </h1>
        <p className="credenciales__intro-subtitle">
          {textServices.findByText('credenciales/intro/subtitleLine1', 'Todo empieza con un')}
          <br />
          {textServices.findByText('credenciales/intro/subtitleLine2', 'solo ingrediente.')}
        </p>
        <span className="credenciales__line credenciales__intro-line credenciales__intro-line--first" aria-hidden="true" />
        <p className="credenciales__intro-simple">
          <em>{textServices.findByText('credenciales/intro/simpleEm', 'Simple:')}</em>{' '}
          {textServices.findByText('credenciales/intro/simple', 'está hecha con')}
        </p>
        <p className="credenciales__intro-malt">
          {textServices.findByText('credenciales/intro/malt', '100% pura malta.')}
        </p>
        <button type="button" className="home-v3__button credenciales__intro-cta" onClick={handleCta}>
          {textServices.findByText('credenciales/intro/cta', '¿Cómo conseguirla?')}
        </button>
        <span className="credenciales__line credenciales__intro-line credenciales__intro-line--second" aria-hidden="true" />
        <p className="credenciales__intro-text">
          {textServices.findByText('credenciales/intro/textStart', 'Sus ')}
          <strong>{textServices.findByText('credenciales/intro/textBold', 'ingredientes naturales')}</strong>
          {textServices.findByText(
            'credenciales/intro/textEnd',
            ' y su máxima pureza son la clave detrás de nuestro sabor único y balanceado.'
          )}
        </p>
      </div>
    </section>
  )
}

export default IntroSection
